/**
 * The two lint rules that judge text against the clock: copy that never appears at all (an error) and
 * copy held too briefly to read (a warning). Both *measure* visibility — seeking the timeline and
 * reading rendered opacity — rather than trusting author-declared attributes, which HtmlAsset
 * compositions don't have. Reading time ports codeo's math (~15 chars/sec, 1.2s floor, ±0.05s
 * tolerance), converting the measured progress-span to seconds via the scene's real playback duration.
 */
import { effectiveOpacity, ownElementText } from './measure';
import type { GsapTimelineLike } from './types';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

// A perceptual floor plus a ~15 chars/sec reading rate.
const MIN_READ_TIME_S = 1.2;
const READING_CHARS_PER_SEC = 15;

// Slack so a borderline window doesn't flip-flop between runs.
export const READING_TIME_TOLERANCE_S = 0.05;

/** Seconds a viewer needs to comfortably read `chars` characters. */
export function requiredReadTimeS(chars: number): number {
    return Math.max(MIN_READ_TIME_S, 0.4 + chars / READING_CHARS_PER_SEC);
}

/** Whether text of `chars` shown for `visibleSeconds` is too brief to read (within tolerance). */
export function readsTooFast(visibleSeconds: number, chars: number): boolean {
    return visibleSeconds + READING_TIME_TOLERANCE_S < requiredReadTimeS(chars);
}

/**
 * Longest contiguous visible span in progress units (0..1) — the first-to-last progress of the run.
 * Multiple appearances yield the longest run; a single-sample flash and empty input yield 0.
 */
export function longestVisibleSpanProgress(
    samples: ReadonlyArray<{ progress: number; visible: boolean }>,
): number {
    let longest = 0;
    let runStart: number | null = null;
    for (const { progress, visible } of samples) {
        if (visible) {
            if (runStart === null) {
                runStart = progress;
            }
            longest = Math.max(longest, progress - runStart);
        } else {
            runStart = null;
        }
    }
    return longest;
}

// Visibility is sampled three times per second of playback, so the grid resolves the same window on a
// short scene as on a long one. Copy that slips between two seeks was up for under a third of a
// second — too fast to read.
const TARGET_SAMPLE_SPACING_MS = 333;
// Bounds on the seek count: enough to say anything about a very short scene, capped so a long one
// doesn't pay for hundreds of seeks and the layout each forces.
const MIN_SAMPLE_COUNT = 20;
const MAX_SAMPLE_COUNT = 200;
// Below this opacity, a fade's dim frames don't count as legible read time.
const VISIBLE_OPACITY = 0.5;
const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
]);

interface TextLeaf {
    element: Element;
    chars: number;
    preview: string;
}

// Elements rendering their own copy, minus ambient text (watermarks/motifs), which isn't read.
function collectTextLeaves(document: Document): TextLeaf[] {
    const body = document.body;
    if (!body) {
        return [];
    }
    const leaves: TextLeaf[] = [];
    for (const element of Array.from(body.querySelectorAll('*'))) {
        if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') {
            continue;
        }
        if (element.closest('[data-l5-text-role="ambient"]')) {
            continue;
        }
        const text = ownElementText(element);
        if (!text) {
            continue;
        }
        leaves.push({ element, chars: text.length, preview: text });
    }
    return leaves;
}

interface BoxLike {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

// A zero-area box (mid-layout, or jsdom's no-layout rects) gets the benefit of the doubt, so this only
// excludes text genuinely animated off-screen.
function intersectsCanvas(
    box: BoxLike,
    canvas: { width: number; height: number } | undefined,
): boolean {
    if (!canvas) {
        return true;
    }
    if (box.width <= 0 || box.height <= 0) {
        return true;
    }
    return box.left < canvas.width && box.right > 0 && box.top < canvas.height && box.bottom > 0;
}

interface VisibilitySample {
    progress: number;
    visible: boolean;
}

interface LeafVisibility {
    leaf: TextLeaf;
    samples: VisibilitySample[];
}

/** How many seeks to take: one per `TARGET_SAMPLE_SPACING_MS` of playback, within the bounds above. */
function sampleCount(sceneDurationMs: number | undefined): number {
    if (
        typeof sceneDurationMs !== 'number' ||
        !Number.isFinite(sceneDurationMs) ||
        sceneDurationMs <= 0
    ) {
        return MIN_SAMPLE_COUNT;
    }
    const spaced = Math.ceil(sceneDurationMs / TARGET_SAMPLE_SPACING_MS) + 1;
    return Math.min(MAX_SAMPLE_COUNT, Math.max(MIN_SAMPLE_COUNT, spaced));
}

/** Whether this leaf is legible right now: bright enough to read, and inside the frame. */
function isLegible(leaf: TextLeaf, canvasSize: { width: number; height: number } | undefined) {
    return (
        effectiveOpacity(leaf.element) >= VISIBLE_OPACITY &&
        intersectsCanvas(leaf.element.getBoundingClientRect(), canvasSize)
    );
}

/**
 * A `window.__timelines.main` these rules can actually measure. The composition declares that object
 * itself, so it can be anything — a stub with only `progress`, or a timeline from another GSAP build —
 * and a rule that assumed otherwise would throw and take every finding of the run down with it.
 */
function measurableTimeline(context: RuntimeLintContext): GsapTimelineLike | null {
    const { timeline } = context;
    if (
        !timeline ||
        typeof timeline.duration !== 'function' ||
        typeof timeline.time !== 'function' ||
        typeof timeline.seek !== 'function'
    ) {
        return null;
    }
    return timeline;
}

/** Seek to `count` evenly-spaced points, recording where each leaf is legible. */
function measureVisibility(
    context: RuntimeLintContext,
    leaves: TextLeaf[],
    count: number,
): VisibilitySample[][] {
    const { canvasSize } = context;
    const timeline = measurableTimeline(context);
    const duration = timeline ? timeline.duration() : 0;
    const samples: VisibilitySample[][] = leaves.map(() => []);
    if (!timeline || !(duration > 0) || leaves.length === 0) {
        return samples;
    }
    // Scene progress, so a measured span converts to screen seconds with the scene's duration no
    // matter how the host maps the two clocks. Falling back to the timeline's own axis keeps
    // hand-built contexts working, at the cost of being right only when the two lengths agree.
    const poseAt = context.seek ?? ((progress: number) => timeline.seek(progress * duration));
    const originalTime = timeline.time();
    try {
        for (let i = 0; i < count; i++) {
            const progress = i / (count - 1);
            poseAt(progress);
            leaves.forEach((leaf, index) => {
                samples[index].push({ progress, visible: isLegible(leaf, canvasSize) });
            });
        }
    } finally {
        timeline.seek(originalTime);
    }
    return samples;
}

// Both rules below judge text against the clock, and the runtime rules run in sequence against one
// loaded composition — so the pass is memoized on the context and whichever rule asks second reuses
// it. The asset builds a fresh context per lint, so the cache can't go stale.
const visibilityByContext = new WeakMap<RuntimeLintContext, LeafVisibility[]>();

function sampleTextVisibility(context: RuntimeLintContext): LeafVisibility[] {
    const cached = visibilityByContext.get(context);
    if (cached) {
        return cached;
    }
    const leaves = measurableTimeline(context) ? collectTextLeaves(context.document) : [];
    const samples = measureVisibility(context, leaves, sampleCount(context.sceneDurationMs));
    const measured = leaves
        .map((leaf, index) => ({ leaf, samples: samples[index] }))
        .filter(({ samples: taken }) => taken.length > 0);
    visibilityByContext.set(context, measured);
    return measured;
}

/** Copy legible at no sampled moment — it never renders, or renders too briefly to be read. */
function neverVisible(entry: LeafVisibility): boolean {
    return !entry.samples.some((sample) => sample.visible);
}

/**
 * Copy the viewer never sees, where the graphic says something. No other rule catches it: the
 * geometry and contrast rules skip elements that measure invisible, so a composition can lose most of
 * its content and still lint clean. Scoped to the same non-ambient text leaves reading time judges —
 * an element with no copy of its own may be a deliberately hidden layer.
 */
export const textNeverVisible: RuntimeLintRule = {
    id: 'text-never-visible',
    severity: 'error',
    guidance:
        'Every piece of on-screen copy must appear, and hold long enough to read. The usual way it ' +
        'fails to: a build-time `gsap.set(el, { opacity: 0 })` under an entrance `from`/`fromTo` that ' +
        'starts at `opacity: 0` cancels itself out — `from` animates to the element’s current value, ' +
        'so the tween runs 0 → 0. Drop the `set` (a hidden from-state already holds the element back) ' +
        'or give the entrance a visible end-state with `fromTo`.',
    check(context: RuntimeLintContext): string[] {
        return sampleTextVisibility(context)
            .filter(neverVisible)
            .map(({ leaf }) => {
                const preview =
                    leaf.preview.length > 40 ? `${leaf.preview.slice(0, 37)}…` : leaf.preview;
                return (
                    `"${preview}" is never on screen to be read. Either its entrance never lands — a ` +
                    `build-time \`gsap.set(el, { opacity: 0 })\` under a \`from\`/\`fromTo\` from ` +
                    `\`opacity: 0\` runs 0 → 0, so drop the \`set\` or give the entrance a visible ` +
                    `end-state with \`fromTo\` — or it flashes past in under a third of a second, in ` +
                    `which case hold it long enough to read.`
                );
            });
    },
};

/**
 * Flags text whose longest legible span is shorter than its read time, via a dense seek-and-measure
 * pass converted to seconds with the real `sceneDurationMs`. Warning severity. Returns [] when it
 * can't judge honestly: no real duration, no seekable timeline, or no text.
 */
export const textReadingTime: RuntimeLintRule = {
    id: 'text-reading-time',
    severity: 'warning',
    guidance:
        'Hold readable text on screen long enough to actually read (≈15 chars/sec, min ~1.2s). If a ' +
        'piece is shown too briefly: when the scene’s duration is flexible (text-on-media) lengthen ' +
        'the scene; when it is fixed by a voiceover, simplify the design — fewer words, larger, or ' +
        'sequence pieces so each holds longer. Mark genuinely-ambient text (watermarks, kinetic ' +
        'background motifs) `data-l5-text-role="ambient"` to waive this.',
    check(context: RuntimeLintContext): string[] {
        const { sceneDurationMs } = context;
        if (
            typeof sceneDurationMs !== 'number' ||
            !Number.isFinite(sceneDurationMs) ||
            sceneDurationMs <= 0
        ) {
            return [];
        }
        const sceneDurationSeconds = sceneDurationMs / 1000;

        const messages: string[] = [];
        for (const entry of sampleTextVisibility(context)) {
            // Copy that shows at no sampled moment is text-never-visible's error, not a reading
            // window this rule can say anything useful about.
            if (neverVisible(entry)) {
                continue;
            }
            const { leaf, samples } = entry;
            // A run undercounts the true window by up to half a sample interval each side (visibility
            // starts/ends between samples). Add one interval back so a genuinely-long-enough window
            // isn't flagged — biasing the heuristic away from false positives.
            const sampleInterval = 1 / (samples.length - 1);
            const spanProgress = Math.min(1, longestVisibleSpanProgress(samples) + sampleInterval);
            const spanSeconds = spanProgress * sceneDurationSeconds;
            if (!readsTooFast(spanSeconds, leaf.chars)) {
                continue;
            }
            const preview =
                leaf.preview.length > 40 ? `${leaf.preview.slice(0, 37)}…` : leaf.preview;
            messages.push(
                `"${preview}" (${leaf.chars} chars) is on screen for only ~${spanSeconds.toFixed(2)}s ` +
                    `but needs ~${requiredReadTimeS(leaf.chars).toFixed(2)}s to read comfortably. Hold ` +
                    `it longer (lengthen the scene if its duration is flexible, or sequence it so it ` +
                    `stays up longer), shorten the copy, or mark it data-l5-text-role="ambient" if it ` +
                    `is decorative and not meant to be read.`,
            );
        }
        return messages;
    },
};
